import {
  BulbOutlined,
  CrownOutlined,
  DashboardOutlined,
  DesktopOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  GroupOutlined,
  IdcardOutlined,
  MessageOutlined,
  ScissorOutlined,
  SendOutlined,
  SoundOutlined,
  VideoCameraAddOutlined,
} from '@ant-design/icons'
import { Alert, Layout, Menu, Result } from 'antd'
import type { MenuProps } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import SaasBacksideBrandLogo from '../ui/SaasBacksideBrandLogo'
import TenantAccountMenu from '../accountMenu/TenantAccountMenu'
import '../ui/tenant-shell.css'
import { CanvasTransitionSurface, WorkspaceRevealBackdrop } from './CanvasTransitionSurface'
import { CanvasWorkspaceTransitionProvider } from './canvasWorkspaceTransition'
import MembershipFlowHost from './contentProduction/canvas/membership/MembershipFlowHost'
import { MembershipFlowProvider } from './contentProduction/canvas/membership/membershipFlowContext'
import { WorkspaceProvider, useWorkspaceState } from './WorkspaceContext'
import ForcePasswordChangeModal from './team/ForcePasswordChangeModal'
import { canAccessTeamManagement } from './team/teamPermissions'
import { WEBSITE_NAV_ITEMS } from './website/websiteNav'
import {
  AUDIO_SUB_ITEMS,
  filterNavSubItems,
  IMAGES_SUB_ITEMS,
  resolveSelectedAudioSubKey,
  resolveSelectedImagesSubKey,
  resolveSelectedTextSubKey,
  resolveSelectedVideoGenSubKey,
  TEXT_SUB_ITEMS,
  VIDEO_GEN_SUB_ITEMS,
  WEBSITE_OPS_SUB_ITEMS,
} from './workspaceNavConfig'

const { Header, Sider, Content } = Layout

const TOP_KEYS = [
  'overview',
  'ai-chat',
  'virtual-ip',
  'virtual-ip-positioning',
  'images',
  'video-generation',
  // VISTOERA_TENANT_AUDIO_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1 —
  // sibling of `images`/`video-generation`; the actual page route is
  // `audio-assets` (see `AUDIO_SUB_ITEMS`), matched via `startsWith`
  // below same as every other top-level key.
  'audio',
  // VISTOERA_TENANT_TEXT_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1 —
  // sibling of `images`/`video-generation`/`audio`; the actual page route
  // is `text-assets` (see `TEXT_SUB_ITEMS`), matched via `startsWith`
  // below same as every other top-level key.
  'text-assets',
  // VISTOERA_CONTENT_PRODUCTION_WORKBENCH_V1 — parent nav key is
  // `content-production-center`; path prefix remains `content-production`.
  'content-production',
  // VISTOERA_COMPETITIVE_CONTENT_ANALYSIS_V1 §2 — sibling to (never
  // nested under) 内容生产中心/内容发布中心.
  'competitive-content-analysis',
  // VISTOERA_CONTENT_PUBLISHING_CENTER_UI_AND_DOMAIN_V1 §5 — sibling to
  // (never nested under) 内容生产中心/视频/网站.
  'content-publishing',
  // VISTOERA_LOCAL_PUBLISHER_ENROLLMENT_GENERATION_PATH_AND_ADMIN_UI_P0 —
  // sibling of `content-publishing` (workspace-scoped device management,
  // not the global/cross-tenant Platform Admin "Worker 节点" list).
  'local-publishers',
  'website',
  'domains',
  'publish',
  // VistoeraTASK-00525 — 团队管理 (Owner/Admin of a Team-Mode Workspace only).
  'team',
] as const

function WorkspaceShellInner({ workspaceId }: { workspaceId: string }) {
  const { workspace, sites, primarySite, entitlements, loading, error } = useWorkspaceState()
  const location = useLocation()
  const navigate = useNavigate()
  // Tenant SaaS backside left navigation — all labels via `navigation` namespace.
  const { t } = useTranslation('common')
  const { t: tNav } = useTranslation('navigation')

  const hasFeature = (key: string) => (loading ? true : Boolean(entitlements[key]))

  const siteIdInUrl = useMemo(() => {
    const m = location.pathname.match(/\/website\/([^/]+)/)
    return m ? m[1] : undefined
  }, [location.pathname])
  const currentSiteId = siteIdInUrl ?? primarySite?.id

  const websiteBase = currentSiteId ? `/workspace/${workspaceId}/website/${currentSiteId}` : `/workspace/${workspaceId}/website`

  const imagesBase = `/workspace/${workspaceId}/images`
  const onImagesSection = location.pathname.startsWith(imagesBase)

  const videoGenBase = `/workspace/${workspaceId}/video-generation`
  const onVideoGenSection = location.pathname.startsWith(videoGenBase)

  const audioBase = `/workspace/${workspaceId}/audio-assets`
  const onAudioSection = location.pathname.startsWith(audioBase)

  // VISTOERA_TENANT_TEXT_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1 §5/
  // §7 — a flat top-level route (mirrors `imagesBase`'s own "the asset
  // page IS the top-level route" shape) since 文本 has exactly one child
  // this round.
  const textAssetsBase = `/workspace/${workspaceId}/text-assets`
  const onTextSection = location.pathname.startsWith(textAssetsBase)

  const contentProductionBase = `/workspace/${workspaceId}/content-production`
  const onContentProductionSection = location.pathname.startsWith(contentProductionBase)

  const virtualIpBase = `/workspace/${workspaceId}/virtual-ip`
  const onVirtualIpSection =
    location.pathname.startsWith(virtualIpBase) ||
    location.pathname.startsWith(`/workspace/${workspaceId}/virtual-ip-positioning`)

  // VISTOERA_CONTENT_PROJECT_DIRECT_CANVAS_AND_FULLSCREEN_CANVAS_SHELL_V1
  // §14/§15 — the Canvas is now a FULL VIEWPORT application surface: it
  // renders with no `Sider`/`Header` at all (see the early return below),
  // not merely with `.tc-content`'s padding stripped (the previous
  // VISTOERA_PRODUCTION_CANVAS_V1_FOUNDATION_AND_INTERACTIVE_SHELL
  // behavior). Still just a Route inside this same Vistoera SPA/shell
  // component tree (never `requestFullscreen()` — spec §15), and the
  // global `Sider`/`Header` JSX below is simply not rendered for this
  // one route rather than deleted as a component.
  // VISTOERA_CANVAS_AS_PRIMARY_HOME_AND_GLOBAL_ACCOUNT_ENTRY_V1 §46 — the
  // `/home` entry point (WorkspaceHomeEntryPage) resolves-then-redirects
  // onto this exact same canonical Canvas route; rendering it in the same
  // fullscreen/no-Sider shell avoids a one-frame Sider+Header flash
  // before the redirect completes.
  // VistoeraTASK-00582 — `/home` and the legacy `/canvas-resume` alias must
  // use the fullscreen Canvas shell (no Sider/Header) so account switch never
  // flashes Workbench while resolving last-opened / Home Canvas.
  const onProductionCanvas =
    /\/content-production\/[^/]+\/canvas(?:\/|$)/.test(location.pathname) ||
    /\/home$/.test(location.pathname) ||
    /\/canvas-resume$/.test(location.pathname)

  // VISTOERA_CANVAS_TO_TENANT_BACKOFFICE_RETURN_STATE_RESTORE_SKELETON_
  // FLASH_ELIMINATION_AND_LAST_BACKOFFICE_LOCATION_MEMORY_P0_V1 §15-§18/
  // §91-§96 — restores the scroll position `CanvasBrandEntry` resolved
  // and rode along as one-shot router `state` (never re-read from
  // storage here — same one-shot convention as `ENTER_FROM_WORKSPACE_STATE`
  // above). Must wait until the target route's REAL content has grown
  // the page tall enough to reach that offset (spec §91/§92 "不能 navigate
  // 后立即 scrollTop=oldValue 但内容尚未 mount") — bounded `requestAnimationFrame`
  // polling (never `setTimeout(500)`, never per-frame React state — spec
  // §93/§95) retries for up to ~1.5s, clamping to whatever max scroll is
  // currently reachable (spec §96), then gives up gracefully.
  useEffect(() => {
    if (onProductionCanvas) return
    const state = location.state as { tenantBackofficeReturnScrollY?: number } | null
    const targetScrollY = state?.tenantBackofficeReturnScrollY
    if (!targetScrollY || targetScrollY <= 0 || typeof window === 'undefined') return
    let cancelled = false
    let frameId = 0
    const startedAt = performance.now()
    const attempt = () => {
      if (cancelled) return
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      window.scrollTo(0, Math.min(targetScrollY, maxScroll))
      const reached = maxScroll >= targetScrollY - 1
      const timedOut = performance.now() - startedAt > 1500
      if (!reached && !timedOut) {
        frameId = requestAnimationFrame(attempt)
      }
    }
    frameId = requestAnimationFrame(attempt)
    return () => {
      cancelled = true
      if (frameId) cancelAnimationFrame(frameId)
    }
    // `location.key` (not just `.pathname`) so a repeat navigation to
    // the exact same pathname (unlikely, but not impossible) still gets
    // its own restore attempt rather than being skipped as "unchanged".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, onProductionCanvas])

  const onWebsiteOpsSection = WEBSITE_OPS_SUB_ITEMS.some((item) =>
    location.pathname.startsWith(`/workspace/${workspaceId}/${item.key}`),
  )
  const onWebsiteSection =
    location.pathname.startsWith(`/workspace/${workspaceId}/website`) || onWebsiteOpsSection

  const [openKeys, setOpenKeys] = useState<string[]>([
    'virtual-ip',
    'images',
    'video-generation',
    'audio',
    'text',
    'content-production-center',
    'website',
  ])

  useEffect(() => {
    if (onVirtualIpSection) {
      setOpenKeys((prev) => (prev.includes('virtual-ip') ? prev : [...prev, 'virtual-ip']))
    }
  }, [onVirtualIpSection])

  useEffect(() => {
    if (onImagesSection) {
      setOpenKeys((prev) => (prev.includes('images') ? prev : [...prev, 'images']))
    }
  }, [onImagesSection])

  useEffect(() => {
    if (onVideoGenSection) {
      setOpenKeys((prev) => (prev.includes('video-generation') ? prev : [...prev, 'video-generation']))
    }
  }, [onVideoGenSection])

  useEffect(() => {
    if (onAudioSection) {
      setOpenKeys((prev) => (prev.includes('audio') ? prev : [...prev, 'audio']))
    }
  }, [onAudioSection])

  useEffect(() => {
    if (onTextSection) {
      setOpenKeys((prev) => (prev.includes('text') ? prev : [...prev, 'text']))
    }
  }, [onTextSection])

  useEffect(() => {
    if (onContentProductionSection) {
      setOpenKeys((prev) =>
        prev.includes('content-production-center') ? prev : [...prev, 'content-production-center'],
      )
    }
  }, [onContentProductionSection])

  useEffect(() => {
    if (onWebsiteSection) {
      setOpenKeys((prev) => (prev.includes('website') ? prev : [...prev, 'website']))
    }
  }, [onWebsiteSection])

  const websiteContentEnabled = hasFeature('feature.website')

  const websiteSiteChildren =
    sites.length === 0 || !currentSiteId || !websiteContentEnabled
      ? []
      : WEBSITE_NAV_ITEMS.map((item) => ({
          key: `website:${item.key}`,
          label: <span className="tc-nav-images-child-label">{tNav(item.labelKey)}</span>,
          className: 'tc-nav-images-child',
        }))

  const websiteOpsChildren = WEBSITE_OPS_SUB_ITEMS.filter(
    (item) => item.featureKey && hasFeature(item.featureKey),
  ).map((item) => ({
    key: item.key,
    label: <span className="tc-nav-images-child-label">{tNav(item.labelKey)}</span>,
    className: 'tc-nav-images-child',
  }))

  const websiteChildren = [...websiteSiteChildren, ...websiteOpsChildren]
  const websiteSectionVisible = websiteContentEnabled || websiteOpsChildren.length > 0

  const imagesSubItemsVisible = filterNavSubItems(IMAGES_SUB_ITEMS, hasFeature)
  const videoGenSubItemsVisible = filterNavSubItems(VIDEO_GEN_SUB_ITEMS, hasFeature)
  const audioSubItemsVisible = filterNavSubItems(AUDIO_SUB_ITEMS, hasFeature)
  // 文本资产 is ungated (no featureKey) — same precedent as IMAGES_SUB_
  // ITEMS'/VIDEO_GEN_SUB_ITEMS'/AUDIO_SUB_ITEMS' own 'assets' entries (a
  // library view over existing canonical content, not itself a gated
  // generation capability).
  const textSubItemsVisible = filterNavSubItems(TEXT_SUB_ITEMS, hasFeature)

  const mapSubChildren = (
    prefix: 'images' | 'video-generation' | 'audio' | 'text',
    items: Array<{ key: string; labelKey: string }>,
  ) =>
    items.map((item) => ({
      key: `${prefix}:${item.key}`,
      label: <span className="tc-nav-images-child-label">{tNav(item.labelKey)}</span>,
      className: 'tc-nav-images-child',
    }))

  const menuItems: MenuProps['items'] = [
    { key: 'overview', icon: <DashboardOutlined />, label: tNav('tenantNav.overview') },
    {
      key: 'content-production-center',
      icon: <BulbOutlined />,
      className: onContentProductionSection
        ? 'tc-nav-images-parent is-section-open'
        : 'tc-nav-images-parent',
      label: tNav('tenantNav.contentProduction.label'),
      children: [
        {
          key: 'content-production-center:projects',
          label: <span className="tc-nav-images-child-label">{tNav('tenantNav.contentProduction.projects')}</span>,
          className: 'tc-nav-images-child',
        },
      ],
    },
    videoGenSubItemsVisible.length > 0
      ? {
          key: 'video-generation',
          icon: <VideoCameraAddOutlined />,
          className: onVideoGenSection ? 'tc-nav-images-parent is-section-open' : 'tc-nav-images-parent',
          label: tNav('tenantNav.video.label'),
          children: mapSubChildren('video-generation', videoGenSubItemsVisible),
        }
      : null,
    imagesSubItemsVisible.length > 0
      ? {
          key: 'images',
          icon: <ScissorOutlined />,
          className: onImagesSection ? 'tc-nav-images-parent is-section-open' : 'tc-nav-images-parent',
          label: tNav('tenantNav.images.label'),
          children: mapSubChildren('images', imagesSubItemsVisible),
        }
      : null,
    // VISTOERA_TENANT_AUDIO_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1
    // §6 — new sibling top-level entry, same "single icon + inline
    // children" shape as 图片/视频 above.
    audioSubItemsVisible.length > 0
      ? {
          key: 'audio',
          icon: <SoundOutlined />,
          className: onAudioSection ? 'tc-nav-images-parent is-section-open' : 'tc-nav-images-parent',
          label: tNav('tenantNav.audio.label'),
          children: mapSubChildren('audio', audioSubItemsVisible),
        }
      : null,
    // VISTOERA_TENANT_TEXT_ASSET_CENTER_CANONICAL_UNIFIED_LIBRARY_P0_V1
    // §5/§6 — new sibling top-level entry, same "single icon + inline
    // children" shape as 图片/视频/音频 above. Only one child (文本资产) this
    // round; 创意库/剧本库/分镜库/文案库 are deliberately NOT separate sidebar
    // destinations (spec §5) — they surface as Type tabs inside the page.
    textSubItemsVisible.length > 0
      ? {
          key: 'text',
          icon: <FileTextOutlined />,
          className: onTextSection ? 'tc-nav-images-parent is-section-open' : 'tc-nav-images-parent',
          label: tNav('tenantNav.text.label'),
          children: mapSubChildren('text', textSubItemsVisible),
        }
      : null,
    {
      key: 'virtual-ip',
      icon: <IdcardOutlined />,
      className: onVirtualIpSection ? 'tc-nav-images-parent is-section-open' : 'tc-nav-images-parent',
      label: tNav('tenantNav.virtualIp.label'),
      children: [
        {
          key: 'virtual-ip:assets',
          label: <span className="tc-nav-images-child-label">{tNav('tenantNav.virtualIp.assets')}</span>,
          className: 'tc-nav-images-child',
        },
        {
          key: 'virtual-ip:projects',
          label: <span className="tc-nav-images-child-label">{tNav('tenantNav.virtualIp.projects')}</span>,
          className: 'tc-nav-images-child',
        },
      ],
    },
    { key: 'competitive-content-analysis', icon: <FileSearchOutlined />, label: tNav('tenantNav.competitiveContentAnalysis') },
    { key: 'content-publishing', icon: <SendOutlined />, label: tNav('tenantNav.contentPublishing') },
    { key: 'local-publishers', icon: <DesktopOutlined />, label: tNav('tenantNav.localPublishers') },
    websiteSectionVisible
      ? {
          key: 'website',
          icon: <GroupOutlined />,
          className: onWebsiteOpsSection
            ? 'tc-nav-images-parent is-section-open'
            : onWebsiteSection
              ? 'tc-nav-images-parent is-section-open'
              : 'tc-nav-images-parent',
          label: tNav('tenantNav.website.label'),
          children: websiteChildren.length > 0 ? websiteChildren : undefined,
        }
      : null,
    hasFeature('feature.ai_chat') ? { key: 'ai-chat', icon: <MessageOutlined />, label: tNav('tenantNav.aiChat') } : null,
    // VistoeraTASK-00525 — always the LAST entry. Shown only when Team Mode is
    // entitled AND the caller is Owner/Admin (display gate; the backend
    // re-authorises every /team/* call from the live membership row).
    canAccessTeamManagement(workspace)
      ? { key: 'team', icon: <CrownOutlined />, label: tNav('tenantNav.teamManagement') }
      : null,
  ]

  const selected =
    TOP_KEYS.slice()
      .reverse()
      .find((key) => location.pathname.startsWith(`/workspace/${workspaceId}/${key}`)) ?? 'overview'
  const selectedWebsiteSubKey = WEBSITE_NAV_ITEMS.map((item) => `website:${item.key}`).find(
    (key) => location.pathname.includes(`/${key.split(':')[1]}`) && location.pathname.startsWith(websiteBase),
  )
  const selectedWebsiteOpsSubKey = WEBSITE_OPS_SUB_ITEMS.map((item) => item.key).find((key) =>
    location.pathname.startsWith(`/workspace/${workspaceId}/${key}`),
  )
  const selectedImagesSubKey = resolveSelectedImagesSubKey(location.pathname, imagesBase)
  const selectedVideoGenSubKey = resolveSelectedVideoGenSubKey(location.pathname, videoGenBase)
  const selectedAudioSubKey = resolveSelectedAudioSubKey(location.pathname, audioBase)
  const selectedTextSubKey = resolveSelectedTextSubKey(location.pathname, textAssetsBase)
  const selectedContentProductionSubKey = onContentProductionSection
    ? 'content-production-center:projects'
    : undefined
  const selectedVirtualIpSubKey = onVirtualIpSection
    ? location.pathname.includes('/virtual-ip/assets')
      ? 'virtual-ip:assets'
      : 'virtual-ip:projects'
    : undefined

  const selectedKeys = selectedWebsiteSubKey
    ? [selectedWebsiteSubKey]
    : selectedWebsiteOpsSubKey
      ? [selectedWebsiteOpsSubKey]
      : selectedImagesSubKey
        ? [selectedImagesSubKey]
        : selectedVideoGenSubKey
          ? [selectedVideoGenSubKey]
          : selectedAudioSubKey
            ? [selectedAudioSubKey]
            : selectedTextSubKey
              ? [selectedTextSubKey]
              : selectedContentProductionSubKey
                ? [selectedContentProductionSubKey]
                : selectedVirtualIpSubKey
                  ? [selectedVirtualIpSubKey]
                  : [selected]

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'virtual-ip') {
      // Parent only expands/collapses — never jump into STEP 1.
      return
    }
    if (key.startsWith('virtual-ip:')) {
      const sub = key.slice('virtual-ip:'.length)
      navigate(`/workspace/${workspaceId}/virtual-ip/${sub === 'assets' ? 'assets' : 'projects'}`)
      return
    }
    if (key.startsWith('website:')) {
      navigate(`${websiteBase}/${key.slice('website:'.length)}`)
      return
    }
    if (key.startsWith('images:')) {
      const subKey = key.slice('images:'.length)
      const item = IMAGES_SUB_ITEMS.find((i) => i.key === subKey)
      navigate(`/workspace/${workspaceId}/${item ? item.path : 'images'}`)
      return
    }
    if (key.startsWith('video-generation:')) {
      const subKey = key.slice('video-generation:'.length)
      const item = VIDEO_GEN_SUB_ITEMS.find((i) => i.key === subKey)
      navigate(`/workspace/${workspaceId}/${item ? item.path : 'video-generation'}`)
      return
    }
    if (key.startsWith('audio:')) {
      const subKey = key.slice('audio:'.length)
      const item = AUDIO_SUB_ITEMS.find((i) => i.key === subKey)
      navigate(`/workspace/${workspaceId}/${item ? item.path : 'audio-assets'}`)
      return
    }
    if (key === 'text') {
      // Parent only expands/collapses — same convention as 'virtual-ip'
      // above (single child this round, so there is no bare 'text' page).
      return
    }
    if (key.startsWith('text:')) {
      const subKey = key.slice('text:'.length)
      const item = TEXT_SUB_ITEMS.find((i) => i.key === subKey)
      navigate(`/workspace/${workspaceId}/${item ? item.path : 'text-assets'}`)
      return
    }
    if (key.startsWith('content-production-center:')) {
      navigate(`/workspace/${workspaceId}/content-production`)
      return
    }
    navigate(`/workspace/${workspaceId}/${key}`)
  }

  const workspaceName = loading ? '…' : workspace?.name || t('workspaceFallback')

  if (error) {
    if (error.status === 403) {
      return <Result status="403" title="403" subTitle="您不是该工作区的成员。" />
    }
    return <Alert type="error" message={error.message} showIcon style={{ margin: 24 }} />
  }

  // VISTOERA_CONTENT_PROJECT_DIRECT_CANVAS_AND_FULLSCREEN_CANVAS_SHELL_V1
  // §14/§40.D — the global Sider/Header are simply not rendered for this
  // route (not removed as components — every other workspace route still
  // gets them via the branch below). `WorkspaceProvider` above still
  // wraps this, so `useWorkspaceState()` (sites/entitlements) stays
  // available to the Canvas page tree exactly as before.
  // VISTOERA_GLOBAL_LOADING_AND_OPENING_SPLASH_UX_TUNING_V1 — the Canvas
  // route's opening experience is the dark fullscreen shell + Brand Breath
  // overlay. Never block on antd Skeleton bars here: render the outlet
  // immediately so canvas/bootstrap can proceed under the splash layer.
  // Workspace APIs still load in `WorkspaceProvider` above; child routes
  // only need `workspaceId` from the URL for their own fetches.
  if (onProductionCanvas) {
    return (
      // VISTOERA_WORKSPACE_TO_CANVAS_TRANSITION_ERROR_STATE_FLASH_WHITE_FRAME_AND_FIRST_PAINT_VISUAL_ATOMICITY_FIX_P0_V1
      // §19-§30/§33 root cause B ("WHITE_FRAME"): `.pcv-fullscreen-shell`
      // (the Canvas Surface `CanvasTransitionSurface` wraps) DOES have a
      // guaranteed opaque background, but only while it is actually
      // sitting over the viewport. During the Enter animation it starts
      // life fully `translate3d(100%,0,0)` off-screen right, and during
      // that ~1-frame (or longer, under load) gap `WorkspaceRevealBackdrop`
      // used to be absent entirely (it was gated on the Exit-only
      // `canvasTransitionPhase === 'exiting'`), so nothing painted the
      // viewport except `<html>`'s own theme-unaware
      // `:root { background: var(--bg) }` (`#f5f7fa`, `src/index.css`) —
      // the perceived "白屏". Fixed by always mounting this purely
      // decorative, static (no state/effects/API calls — zero perf cost)
      // backdrop whenever we're on this route, covering BOTH the Enter
      // and Exit windows identically; `.tc-workspace-shell-root` below is
      // the second, defense-in-depth layer (Option A "shared transition
      // host background" — spec §33) for any future gap this doesn't
      // anticipate.
      <div className="tc-workspace-shell-root">
        <WorkspaceRevealBackdrop />
        <CanvasTransitionSurface>
          <Outlet />
        </CanvasTransitionSurface>
      </div>
    )
  }

  return (
    <div className="tc-workspace-shell-root">
      <Layout className="app-shell tc-shell">
        <Sider width={212} theme="light" className="app-sider tc-sider">
          <div className="tc-sider-fixed-header">
            <div className="tc-brand">
              <div className="tc-brand-row">
                <SaasBacksideBrandLogo workspaceId={workspaceId} showBrandTitle />
              </div>
              <span className="tc-brand-workspace">{workspaceName}</span>
            </div>
          </div>
          <div className="tc-sider-nav-scroll">
            <Menu
              theme="light"
              mode="inline"
              className="app-nav-menu tc-nav-menu"
              selectedKeys={selectedKeys}
              openKeys={openKeys}
              onOpenChange={setOpenKeys}
              onClick={onMenuClick}
              items={menuItems}
            />
          </div>
        </Sider>
        <Layout>
          <Header className="app-header tc-header">
            <div className="tc-header-left">
              <span className="tc-context-chip">{t('myWorkspace')}</span>
              <span className="tc-context-chip tc-context-chip-strong">{workspaceName}</span>
            </div>
            <div className="tc-header-right">
              <TenantAccountMenu workspaceId={workspaceId} surface="workbench" />
            </div>
          </Header>
          {/* VISTOERA_CANVAS_TO_TENANT_BACKOFFICE_RETURN_STATE_RESTORE_
           * SKELETON_FLASH_ELIMINATION_AND_LAST_BACKOFFICE_LOCATION_
           * MEMORY_P0_V1 forensic finding: gating the ENTIRE `<Outlet/>`
           * on `WorkspaceProvider`'s own bootstrap `loading` (a SEPARATE
           * generic single-block `<Skeleton/>` from `WorkspaceOverviewPage`'s
           * own two-block one) reproduced a real — if narrower — Skeleton
           * flash of its own: a Canvas opened via direct deep link (fresh
           * page load, `WorkspaceProvider` bootstrap still in flight) whose
           * Logo is clicked before that bootstrap settles. Every route
           * page already gets `workspaceId` straight from the URL (never
           * from this context) for its own fetches, and `hasFeature`
           * above already defaults to `true` while `loading` (the Sider's
           * own precedent for "render immediately, backfill once ready") —
           * so the Outlet can follow the exact same "shell first, data
           * second" contract TASK-00037 already established for the
           * Canvas side, and let each page's own (already-existing)
           * loading/empty state do the rest. */}
          <Content className="app-content tc-content">
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </div>
  )
}

export default function WorkspaceLayout() {
  const { workspaceId = '' } = useParams()
  return (
    <WorkspaceProvider workspaceId={workspaceId}>
      {/* Mounted once per `/workspace/:workspaceId/*` subtree, OUTSIDE the
       * `onProductionCanvas` branch below — persists across the Canvas ↔
       * Workspace route swap itself, which is what lets a single V-logo
       * click (`CanvasBrandEntry`, deep inside the Canvas branch) delay
       * the real `navigate()` by ~260ms while this same component
       * instance keeps rendering the exiting Canvas Surface. */}
      <CanvasWorkspaceTransitionProvider>
        <MembershipFlowProvider>
          <WorkspaceShellInner workspaceId={workspaceId} />
          <MembershipFlowHost workspaceId={workspaceId} />
          {/* VistoeraTASK-00525 — blocks a freshly created Managed Account until it sets its own password. */}
          <ForcePasswordChangeModal />
        </MembershipFlowProvider>
      </CanvasWorkspaceTransitionProvider>
    </WorkspaceProvider>
  )
}
